// DXF to GeoJSON converter
// This converter handles DXF files (text format) which is a stable and well-documented format

/**
 * Convert DXF/DWG file to GeoJSON
 * Note: This function accepts both DXF and DWG files, but only DXF (text format) is fully supported.
 * DXF is a stable text-based format, making this parser robust and reliable for DXF files.
 * Binary DWG format is not supported.
 * @param {File} dxfFile - The DXF/DWG file to convert (DXF format recommended)
 * @returns {Promise<Object>} GeoJSON FeatureCollection
 */

/*
  DXF file format is a text-based format.
  The file is a list of entities.
  Each entity has a type, a layer, and a list of coordinates.

  example: This is a POINT entity with coordinates [0, 0].
  ENTITIES
  0 => code for the entity type
  POINT => entity type
  10 => code for the x coordinate
  0 => value for the x coordinate
  20 => code for the y coordinate
  0 => value for the y coordinate
  ENDSEC => code for the end of the entity
  ENTITIES => code for the start of the entities section
  ENDSEC => code for the end of the entities section
  This is a LINE entity with coordinates [0, 0] and [1, 1].
  ENTITIES
  0 => code for the entity type
  LINE => entity type
  10 => code for the x coordinate
  0 => value for the x coordinate
  20 => code for the y coordinate
  0 => value for the y coordinate
  10 => code for the x coordinate
  1 => value for the x coordinate
  20 => code for the y coordinate
  1 => value for the y coordinate
  ENDSEC => code for the end of the entity
  ENDSEC => code for the end of the entities section
*/

export async function convertDXFToGeoJSON(dxfFile) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        const arrayBuffer = e.target.result;
        const geojson = await parseDXFFile(arrayBuffer, dxfFile.name);
        resolve(geojson);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsArrayBuffer(dxfFile);
  });
}

/**
 * Parse DXF file and extract geometries
 * This parser is robust and reliable for DXF files (text format).
 * DXF is a stable, well-documented format, making this implementation solid for production use.
 * Binary DWG format is not supported.
 * @param {ArrayBuffer} arrayBuffer - File content as ArrayBuffer
 * @param {string} filename - Original filename
 * @returns {Promise<Object>} GeoJSON FeatureCollection
 */
async function parseDXFFile(arrayBuffer, filename) {
  const textDecoder = new TextDecoder('latin1');
  
  // Detect file format: DXF (text)
  // Read only the first 1000 bytes to detect file format
  // This is just for format detection - the full file will be parsed if it's DXF
  const textStart = textDecoder.decode(arrayBuffer.slice(0, Math.min(1000, arrayBuffer.byteLength)));
  
  if (textStart.includes('$ACADVER') || textStart.includes('SECTION')) {
    // It's a DXF file (text format) - parse the entire file
    // DXF is a stable format, so this parser is robust and reliable
    return parseDXF(textDecoder.decode(arrayBuffer));
  }
  
  // Binary DWG format is not supported
  // DXF format is recommended as it's text-based and stable
  throw new Error('Binary DWG format is not supported. Please use DXF format (text-based, stable format).');
}

/**
 * Parse DXF file and convert entities to GeoJSON features
 * DXF is a stable, text-based format with well-documented structure.
 * This parser is robust and reliable for DXF files in production use.
 * Supports: POINT, LINE, LWPOLYLINE, POLYLINE, SPLINE, CIRCLE
 * @param {string} dxfText - DXF file content as text
 * @returns {Object} GeoJSON FeatureCollection
 */
function parseDXF(dxfText) {
  const features = [];
  const lines = dxfText.split(/\r?\n/);
  
  // Track problematic splines for optional logging
  const problematicSplines = [];
  
  // Circle counters for tracking parsing success
  const circleCounters = { circlesCreated: { count: 0 }, circlesSkipped: { count: 0 } };
  
  let currentEntity = null;
  let inEntities = false;
  let currentLayer = '0';
  let entityType = null;
  let currentEntityLog = null;
  
  for (let i = 0; i < lines.length; i++) {
    const code = lines[i].trim();
    const value = i + 1 < lines.length ? lines[i + 1].trim() : '';
    
    // Check for ENTITIES section (standard DXF is: 0 SECTION / 2 ENTITIES)
    if (code === '2' && value === 'ENTITIES') {
      inEntities = true;
      i++; // Skip to next line
      continue;
    }
    
    // End of ENTITIES section (standard DXF is: 0 ENDSEC)
    if (inEntities && code === '0' && value === 'ENDSEC') {
      // Finish current entity before ending section
      if (currentEntity) {
        finishEntity(currentEntity, features, problematicSplines, circleCounters);
        currentEntity = null;
      }
      inEntities = false;
      continue;
    }
    
    if (!inEntities) continue;
    
    // Entity type (code 0)
    if (code === '0') {
      // Finish previous entity before starting a new one
      if (currentEntity) {
        finishEntity(currentEntity, features, problematicSplines, circleCounters);
        currentEntity = null;
        currentEntityLog = null;
      }
      
      // Start new entity
      entityType = value;
      
      // Initialize entity log for tracking (optional, used for problematic entities)
      currentEntityLog = {
        entityType: entityType,
        layer: currentLayer,
        startLine: i,
        codesEncountered: [],
        controlPointsCaptured: [],
        fitPointsCaptured: [],
        coordinatesCaptured: [],
        metadata: {},
        endLine: null,
        finalState: null
      };
      
      if (entityType === 'POINT') {
        currentEntity = { type: 'Point', coordinates: null, layer: currentLayer };
      } else if (entityType === 'LINE') {
        currentEntity = { type: 'LineString', coordinates: [], layer: currentLayer, entityType: 'LINE' };
      } else if (entityType === 'LWPOLYLINE' || entityType === 'POLYLINE') {
        currentEntity = { type: 'LineString', coordinates: [], layer: currentLayer, closed: false, numVertices: null, pendingX: undefined };
      } else if (entityType === 'SPLINE') {
        // SPLINE: convert control points or fit points to LineString (treated as regular LineString)
        // SPLINEs can have control points (code 10/20) or fit points (code 11/21)
        currentEntity = { 
          type: 'LineString', 
          coordinates: [], 
          layer: currentLayer, 
          isSpline: true,
          controlPoints: [],
          fitPoints: [],
          degree: null,
          numKnots: null,
          numControlPoints: null,
          numFitPoints: null,
          flags: null
        };
        currentEntityLog.metadata.isSpline = true;
      } else if (entityType === 'CIRCLE') {
        // CIRCLE: center (codes 10/20, optional 30 for Z) + radius (code 40)
        currentEntity = {
          type: 'Circle',
          center: [null, null],
          radius: null,
          layer: currentLayer,
          entityType: 'CIRCLE'
        };
      } else {
        currentEntity = null; // Skip unsupported entities
        currentEntityLog = null;
      }
      i++; // Skip value line
      continue;
    }
    
    // Layer (code 8) must be handled even for unsupported entities.
    // Otherwise currentLayer can get "stuck" and all subsequent entities inherit the wrong layer.
    if (code === '8') {
      currentLayer = value;
      if (currentEntity) currentEntity.layer = currentLayer;
      if (currentEntityLog) currentEntityLog.layer = currentLayer;
      i++; // skip value line
      continue;
    }

    // IMPORTANT: when skipping unsupported entities (currentEntity == null),
    // we must still keep the DXF group-code/value alignment (2 lines per pair).
    if (!currentEntity) {
      i++; // skip value line
      continue;
    }
    
    // Track codes encountered for debugging problematic entities
    if (currentEntityLog) {
      currentEntityLog.codesEncountered.push({
        code: code,
        value: value,
        lineNumber: i
      });
    }
    
    // Number of vertices for LWPOLYLINE (code 90)
    if (code === '90' && (entityType === 'LWPOLYLINE' || entityType === 'POLYLINE')) {
      currentEntity.numVertices = parseInt(value) || null;
      if (currentEntityLog) {
        currentEntityLog.metadata.numVertices = currentEntity.numVertices;
      }
      i++;
      continue;
    }
    
    // Closed flag for polyline (code 70)
    if (code === '70') {
      if (entityType === 'LWPOLYLINE' || entityType === 'POLYLINE') {
        const flag = parseInt(value) || 0;
        currentEntity.closed = (flag & 1) === 1;
      } else if (entityType === 'SPLINE' && currentEntity.isSpline) {
        // SPLINE flags: 1=closed, 2=periodic, 4=rational, 8=planar, 16=linear
        currentEntity.flags = parseInt(value) || 0;
      }
      i++;
      continue;
    }
    
    // SPLINE-specific codes
    if (entityType === 'SPLINE' && currentEntity && currentEntity.isSpline) {
      // Degree (code 71)
      if (code === '71') {
        currentEntity.degree = parseInt(value) || null;
        i++;
        continue;
      }
      // Number of knots (code 72)
      if (code === '72') {
        currentEntity.numKnots = parseInt(value) || null;
        i++;
        continue;
      }
      // Number of control points (code 73)
      if (code === '73') {
        currentEntity.numControlPoints = parseInt(value) || null;
        if (currentEntityLog) {
          currentEntityLog.metadata.numControlPoints = currentEntity.numControlPoints;
        }
        i++;
        continue;
      }
      // Number of fit points (code 74)
      if (code === '74') {
        currentEntity.numFitPoints = parseInt(value) || null;
        if (currentEntityLog) {
          currentEntityLog.metadata.numFitPoints = currentEntity.numFitPoints;
        }
        i++;
        continue;
      }
      // Knot values (code 40)
      if (code === '40') {
        // Knot values are stored but not used for simple conversion
        // But we should continue parsing - control points come AFTER knots
        i++;
        continue;
      }
      // Control point weights (code 41)
      if (code === '41') {
        // Weights are stored but not used for simple conversion
        // Weights come after knots but before or with control points
        i++;
        continue;
      }
    }
    
    // X coordinate (code 10) - for POINT, LINE, LWPOLYLINE, POLYLINE, SPLINE control points, CIRCLE center
    if (code === '10') {
      const x = parseFloat(value);
      if (!isNaN(x)) {
        // CIRCLE: code 10 is the X coordinate of the center
        if (currentEntity && currentEntity.entityType === 'CIRCLE') {
          if (!Array.isArray(currentEntity.center)) {
            currentEntity.center = [null, null];
          }
          currentEntity.center[0] = x;
          
          // Track coordinate capture for debugging
          if (currentEntityLog) {
            currentEntityLog.coordinatesCaptured = currentEntityLog.coordinatesCaptured || [];
            currentEntityLog.coordinatesCaptured.push({
              point: [x, null],
              index: 0,
              lineNumber: i,
              note: 'CIRCLE center X (code 10)'
            });
          }
        }
        // Store X coordinate temporarily - we'll wait for code 20 (Y) to complete the point
        // For LWPOLYLINE, we need to handle this differently - store X and wait for Y
        if (currentEntity && currentEntity.type === 'LineString' && !currentEntity.isSpline) {
          // For LWPOLYLINE/POLYLINE, store X temporarily and wait for code 20
          currentEntity.pendingX = x;
        } else {
          // For other entities (POINT, LINE, SPLINE), look ahead for Y coordinate
          let y = 0;
          let z = 0; // Z coordinate (code 30, optional)
          
          // Check next lines for Y and Z
          // For SPLINEs, we need to be more careful - Y should be on the very next code/value pair
          let foundY = false;
          for (let j = i + 2; j < Math.min(i + 10, lines.length); j += 2) {
            const nextCode = lines[j]?.trim();
            const nextValue = lines[j + 1]?.trim();
            
            if (nextCode === '20' && nextValue !== undefined) {
              y = parseFloat(nextValue) || 0;
              foundY = true;
              // For SPLINEs, after Y we might have another 10 for the next control point
              // So don't break yet, but we can continue
            } else if (nextCode === '30' && nextValue !== undefined) {
              z = parseFloat(nextValue) || 0;
              break; // Z is usually the last coordinate
            } else if (nextCode === '10' || nextCode === '11') {
              // Another coordinate code - this means we've finished this point
              // For SPLINEs, this is normal - multiple control points use repeated 10/20 pairs
              break;
            } else if (nextCode && !['10', '11', '20', '21', '30', '31', '40', '41', '42'].includes(nextCode)) {
              // If we hit a different code (not coordinate-related), stop looking
              // Note: code 42 is bulge for LWPOLYLINE, we allow it
              break;
            }
          }
          
          if (currentEntity && currentEntity.type === 'Point') {
            currentEntity.coordinates = [x, y];
          } else if (currentEntity && currentEntity.type === 'LineString') {
            if (currentEntity.isSpline) {
              // For SPLINE, code 10/20 are control points
              // We should capture ALL control points, not just the first one
              // The parser will encounter multiple 10/20 pairs for a single SPLINE
              currentEntity.controlPoints.push([x, y]);
              currentEntity.coordinates.push([x, y]);
              
              // Log control point capture
              if (currentEntityLog) {
                currentEntityLog.controlPointsCaptured.push({
                  point: [x, y],
                  index: currentEntity.controlPoints.length - 1,
                  lineNumber: i
                });
              }
              
            }
          }
        }
      }
      i++;
      continue;
    }
    
    // Y coordinate (code 20) - for LWPOLYLINE/POLYLINE vertices, SPLINE control points, or CIRCLE center Y
    if (code === '20') {
      const y = parseFloat(value);
      if (!isNaN(y) && currentEntity) {
        // CIRCLE: code 20 is the Y coordinate of the center
        if (currentEntity && currentEntity.entityType === 'CIRCLE') {
          if (!Array.isArray(currentEntity.center)) {
            currentEntity.center = [null, null];
          }
          currentEntity.center[1] = y;
          
          // Track coordinate capture for debugging
          if (currentEntityLog) {
            currentEntityLog.coordinatesCaptured = currentEntityLog.coordinatesCaptured || [];
            const existingX = currentEntityLog.coordinatesCaptured.find(c => c.note && c.note.includes('CIRCLE center X'));
            if (existingX) {
              existingX.point[1] = y;
              existingX.note = 'CIRCLE center X/Y (codes 10/20)';
            } else {
              currentEntityLog.coordinatesCaptured.push({
                point: [currentEntity.center[0], y],
                index: 0,
                lineNumber: i,
                note: 'CIRCLE center Y (code 20)'
              });
            }
          }
        }
        if (currentEntity.type === 'LineString' && !currentEntity.isSpline && currentEntity.pendingX !== undefined) {
          // For LWPOLYLINE/POLYLINE, complete the vertex with the pending X
          const x = currentEntity.pendingX;
          currentEntity.coordinates.push([x, y]);
          currentEntity.pendingX = undefined; // Clear pending X
          
          // Log coordinate capture
          if (currentEntityLog) {
            currentEntityLog.coordinatesCaptured.push({
              point: [x, y],
              index: currentEntity.coordinates.length - 1,
              lineNumber: i
            });
          }
        } else if (currentEntity.type === 'LineString' && currentEntity.isSpline) {
          // For SPLINE, Y should have been handled with code 10, but handle standalone case
          // This shouldn't happen in standard DXF, but handle it just in case
        }
      }
      i++;
      continue;
    }
    
    // X coordinate (code 11) - for LINE end point or SPLINE fit points
    if (code === '11' && currentEntity) {
      const x = parseFloat(value);
      if (!isNaN(x)) {
        // In DXF, code 21 (Y) should come right after code 11 (X)
        // Look ahead for Y coordinate
        let y = 0;
        let z = 0; // Z coordinate (code 31, optional)
        
        // Check next lines for Y and Z
        for (let j = i + 2; j < Math.min(i + 10, lines.length); j += 2) {
          const nextCode = lines[j]?.trim();
          const nextValue = lines[j + 1]?.trim();
          
          if (nextCode === '21' && nextValue !== undefined) {
            y = parseFloat(nextValue) || 0;
          } else if (nextCode === '31' && nextValue !== undefined) {
            z = parseFloat(nextValue) || 0;
            break; // Z is usually the last coordinate
          } else if (nextCode && !['10', '11', '20', '21', '30', '31'].includes(nextCode)) {
            // If we hit a different code, stop looking
            break;
          }
        }
        
        if (currentEntity.isSpline) {
          // For SPLINE fit points
          currentEntity.fitPoints.push([x, y]);
          
          // Log fit point capture
          if (currentEntityLog) {
            currentEntityLog.fitPointsCaptured.push({
              point: [x, y],
              index: currentEntity.fitPoints.length - 1,
              lineNumber: i
            });
          }
          
          
          // Use fit points if no control points are available
          if (currentEntity.controlPoints.length === 0) {
            currentEntity.coordinates.push([x, y]);
          }
        } else if (currentEntity.type === 'LineString' && currentEntity.entityType === 'LINE') {
          // For LINE entities, code 11/21 is the end point
          // Add it to coordinates
          currentEntity.coordinates.push([x, y]);
          
          // Log coordinate capture
          if (currentEntityLog) {
            currentEntityLog.coordinatesCaptured.push({
              point: [x, y],
              index: currentEntity.coordinates.length - 1,
              lineNumber: i,
              note: 'LINE end point (code 11/21)'
            });
          }
          
        }
      }
      i++;
      continue;
    }
    
    // Fit point Y coordinate (code 21) - skip if already handled with code 11
    if (code === '21') {
      i++;
      continue;
    }

    // Z coordinate (code 30) - for CIRCLE, ignore (we only use X/Y)
    if (code === '30' && currentEntity && currentEntity.entityType === 'CIRCLE') {
      // Z is optional for circles, ignore it
      i++;
      continue;
    }

    // Circle radius (code 40)
    if (code === '40' && currentEntity && currentEntity.entityType === 'CIRCLE') {
      const r = parseFloat(value);
      if (!isNaN(r)) {
        currentEntity.radius = r;
      }
      i++;
      continue;
    }
    
    // Vertex coordinates for POLYLINE (code 10/20 in VERTEX entities)
    if (code === 'VERTEX' && entityType === 'POLYLINE') {
      // Vertex entities are handled separately
      i++;
      continue;
    }
    
    // Default: increment i for any unrecognized codes to prevent infinite loop
    // This ensures the parser always advances, even for codes we don't explicitly handle
    // (e.g., code 100 "AcDbEntity", code 102 extension groups, code 330 soft pointers, etc.)
    i++;
  }
  
  // Finish last entity
  if (currentEntity) {
    finishEntity(currentEntity, features, problematicSplines, circleCounters);
  }
  
  return {
    type: 'FeatureCollection',
    features: features.filter(f => f !== null),
    problematicSplines: problematicSplines.length > 0 ? problematicSplines : undefined
  };
}

/**
 * Finalize entity and add to features collection
 * @param {Object} entity - Entity object to finalize
 * @param {Array} features - Features array to add to
 * @param {Array} problematicSplines - Optional array to track problematic splines
 * @param {Object} circleCounters - Optional counters for circle parsing statistics
 */
function finishEntity(entity, features, problematicSplines = null, circleCounters = null) {
  if (!entity) return;
  
  // Build diagnostic information for SPLINE entities
  let parsingIssues = [];
  let diagnosticInfo = {};
  
  // Track problematic LineString entities (those with only 1 point)
  let entityLog = null;
  if (problematicSplines && entity.type === 'LineString' && entity.coordinates.length === 1) {
    entityLog = {
      entityType: entity.isSpline ? 'SPLINE' : (entity.entityType || 'LineString'),
      layer: entity.layer || '0',
      step1_controlPoints: entity.isSpline ? JSON.parse(JSON.stringify(entity.controlPoints || [])) : [],
      step1_fitPoints: entity.isSpline ? JSON.parse(JSON.stringify(entity.fitPoints || [])) : [],
      step1_coordinates: JSON.parse(JSON.stringify(entity.coordinates || [])),
      step1_metadata: entity.isSpline ? {
        degree: entity.degree,
        numKnots: entity.numKnots,
        numControlPoints: entity.numControlPoints,
        numFitPoints: entity.numFitPoints,
        flags: entity.flags
      } : null,
      isProblematic: true,
      problem: 'Only 1 point at step 1'
    };
    problematicSplines.push(entityLog);
  }
  
  // Track problematic splines specifically
  let splineLog = null;
  if (entity.isSpline && problematicSplines) {
    const hasOnlyOnePoint = (entity.controlPoints.length === 1 && entity.fitPoints.length === 0) ||
                            (entity.controlPoints.length === 0 && entity.fitPoints.length === 1) ||
                            (entity.coordinates.length === 1);
    
    if (hasOnlyOnePoint && !entityLog) {
      splineLog = {
        layer: entity.layer || '0',
        step1_controlPoints: JSON.parse(JSON.stringify(entity.controlPoints || [])),
        step1_fitPoints: JSON.parse(JSON.stringify(entity.fitPoints || [])),
        step1_coordinates: JSON.parse(JSON.stringify(entity.coordinates || [])),
        step1_metadata: {
          degree: entity.degree,
          numKnots: entity.numKnots,
          numControlPoints: entity.numControlPoints,
          numFitPoints: entity.numFitPoints,
          flags: entity.flags
        },
        isProblematic: true,
        problem: 'Only 1 point at step 1'
      };
      problematicSplines.push(splineLog);
    } else if (hasOnlyOnePoint) {
      splineLog = entityLog;
    }
  }
  
  // Handle CIRCLE entities
  if (entity.entityType === 'CIRCLE' || entity.type === 'Circle') {
    const cx = Array.isArray(entity.center) ? entity.center[0] : null;
    const cy = Array.isArray(entity.center) ? entity.center[1] : null;

    if (cx == null || cy == null) {
      // Invalid circle - skip it
      if (circleCounters) circleCounters.circlesSkipped.count++;
      return;
    }
    
    // Valid circle - create feature
    if (circleCounters) circleCounters.circlesCreated.count++;
    
    // Create custom "Circle" geometry in GeoJSON
    // ProjectImportHandler will convert it to Point geometry
    const feature = {
      type: 'Feature',
      geometry: {
        type: 'Circle',
        coordinates: [cx, cy]
      },
      properties: {
        layer: entity.layer || '0',
        radius: entity.radius
      }
    };
    features.push(feature);
    return;
  }

  if (entity.isSpline) {
    diagnosticInfo = {
      entityType: 'SPLINE',
      degree: entity.degree,
      numKnots: entity.numKnots,
      numControlPoints: entity.numControlPoints,
      numFitPoints: entity.numFitPoints,
      flags: entity.flags,
      controlPointsFound: entity.controlPoints.length,
      fitPointsFound: entity.fitPoints.length,
      coordinatesUsed: entity.coordinates.length
    };
    
    // Diagnose parsing issues
    if (entity.numControlPoints !== null && entity.controlPoints.length !== entity.numControlPoints) {
      parsingIssues.push(`Expected ${entity.numControlPoints} control points but found ${entity.controlPoints.length}`);
    }
    if (entity.numFitPoints !== null && entity.fitPoints.length !== entity.numFitPoints) {
      parsingIssues.push(`Expected ${entity.numFitPoints} fit points but found ${entity.fitPoints.length}`);
    }
    if (entity.coordinates.length === 0) {
      parsingIssues.push('No coordinates extracted (neither control points nor fit points found)');
    }
    if (entity.coordinates.length === 1) {
      parsingIssues.push(`Only one coordinate point found: [${entity.coordinates[0][0]}, ${entity.coordinates[0][1]}] - SPLINE should have multiple points. This suggests the parser only captured the first point.`);
    }
    if (entity.controlPoints.length === 0 && entity.fitPoints.length === 0) {
      parsingIssues.push('No control points or fit points found - SPLINE may use unsupported structure or the coordinates were not parsed correctly');
    }
    
    // Special case: if we have exactly 1 control point but expected more, it's likely a parsing issue
    if (entity.controlPoints.length === 1 && entity.numControlPoints !== null && entity.numControlPoints > 1) {
      parsingIssues.push(`CRITICAL: Only 1 control point parsed but ${entity.numControlPoints} expected. The parser likely missed subsequent control points. Possible causes: 1) Control points use a different code structure, 2) Another entity started before all points were read, 3) The DXF structure is non-standard.`);
    }
    
    // Also check if we have exactly 1 point but no expected count (might be a different issue)
    if (entity.controlPoints.length === 1 && entity.numControlPoints === null && entity.numFitPoints === null) {
      parsingIssues.push(`Only 1 control point found and no expected count specified. This might indicate the SPLINE definition is incomplete or uses an unsupported structure.`);
    }
    
    // If no control points but fit points exist, use fit points
    if (entity.controlPoints.length === 0 && entity.fitPoints.length > 0) {
      entity.coordinates = entity.fitPoints;
      diagnosticInfo.coordinatesSource = 'fit_points';
    } else if (entity.controlPoints.length > 0) {
      // Check for duplicate/identical points
      const uniquePoints = [];
      const tolerance = 1e-10;
      let duplicateCount = 0;
      
      for (const point of entity.controlPoints) {
        const isDuplicate = uniquePoints.some(existing => 
          Math.abs(existing[0] - point[0]) < tolerance && 
          Math.abs(existing[1] - point[1]) < tolerance
        );
        if (!isDuplicate) {
          uniquePoints.push(point);
        } else {
          duplicateCount++;
        }
      }
      
      if (duplicateCount > 0) {
        parsingIssues.push(`Found ${duplicateCount} duplicate/identical points. Only ${uniquePoints.length} unique points remain.`);
      }
      
      
      // If all points are identical, keep at least 2 points (first and last) to make a valid LineString
      // Otherwise, use all points (don't filter duplicates automatically - let the user decide)
      if (uniquePoints.length === 1 && entity.controlPoints.length > 1) {
        parsingIssues.push(`CRITICAL: All ${entity.controlPoints.length} control points are identical! Point: [${uniquePoints[0][0]}, ${uniquePoints[0][1]}]`);
        // Keep first and last to make a valid LineString (even if they're the same)
        entity.coordinates = [entity.controlPoints[0], entity.controlPoints[entity.controlPoints.length - 1]];
        diagnosticInfo.coordinatesSource = 'control_points_degenerate';
        diagnosticInfo.allPointsIdentical = true;
        diagnosticInfo.originalPointCount = entity.controlPoints.length;
      } else if (duplicateCount > 0) {
        // Some duplicates but not all - keep all points but log the issue
        entity.coordinates = entity.controlPoints;
        diagnosticInfo.coordinatesSource = 'control_points';
        diagnosticInfo.uniquePointsAfterDedup = uniquePoints.length;
        diagnosticInfo.duplicatePointsRemoved = duplicateCount;
      } else {
        // No duplicates - use all points
        entity.coordinates = entity.controlPoints;
        diagnosticInfo.coordinatesSource = 'control_points';
      }
      
      
      // Track step 2 for problematic entities
      if (entityLog) {
        entityLog.step2_afterAssignment = {
          coordinates: JSON.parse(JSON.stringify(entity.coordinates)),
          coordinatesCount: entity.coordinates.length,
          coordinatesSource: diagnosticInfo.coordinatesSource
        };
      }
      if (splineLog && splineLog !== entityLog) {
        splineLog.step2_afterAssignment = {
          coordinates: JSON.parse(JSON.stringify(entity.coordinates)),
          coordinatesCount: entity.coordinates.length,
          coordinatesSource: diagnosticInfo.coordinatesSource
        };
      }
    } else {
      // Keep whatever coordinates we have (even if just 1)
      diagnosticInfo.coordinatesSource = 'partial_parsing';
      
      // Track step 2 for problematic splines
      if (splineLog) {
        splineLog.step2_afterAssignment = {
          coordinates: JSON.parse(JSON.stringify(entity.coordinates)),
          coordinatesCount: entity.coordinates.length,
          coordinatesSource: diagnosticInfo.coordinatesSource
        };
      }
    }
  }
  
  // Handle Point entities
  if (entity.type === 'Point') {
    // Only create Point if coordinates are valid
    if (entity.coordinates && Array.isArray(entity.coordinates) && entity.coordinates.length >= 2) {
      const feature = createFeature(entity);
      if (feature) {
        if (parsingIssues.length > 0) {
          feature.properties.parsingIssues = parsingIssues.join('; ');
          feature.properties.diagnosticInfo = JSON.stringify(diagnosticInfo);
        }
        features.push(feature);
      }
    }
  } 
  // Handle LineString entities (including LWPOLYLINE, POLYLINE, SPLINE)
  else if (entity.type === 'LineString') {
    // Parser creates LineString only (even if closed)
    // ProjectImportHandler will convert to Polygon if needed
    // Check if polyline is closed (by flag OR by coordinates) for metadata
    const coords = [...entity.coordinates];
    const first = coords[0];
    const last = coords[coords.length - 1];
    const tolerance = 1e-10;
    const coordinatesAreClosed = coords.length >= 2 && 
                                 Math.abs(first[0] - last[0]) < tolerance && 
                                 Math.abs(first[1] - last[1]) < tolerance;
    
    // Always create LineString, but mark if it's closed for ProjectImportHandler
    const isClosed = entity.closed || coordinatesAreClosed;
    
    // Only create feature if we have valid coordinates
    if (coords.length > 0) {
      const feature = {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: coords
        },
        properties: {
          layer: entity.layer || '0',
          type: 'LWPOLYLINE',
          closed: isClosed,  // Flag for ProjectImportHandler to convert to Polygon
          closedBy: entity.closed ? 'flag' : (coordinatesAreClosed ? 'coordinates' : 'open')
        }
      };
      
      if (parsingIssues.length > 0) {
        feature.properties.parsingIssues = parsingIssues.join('; ');
        feature.properties.diagnosticInfo = JSON.stringify(diagnosticInfo);
      }
      
      features.push(feature);
    }
  }
}

/**
 * Create a GeoJSON feature from an entity
 * @param {Object} entity - Entity object
 * @returns {Object|null} GeoJSON feature or null if invalid
 */

function createFeature(entity) {
  let geometry;
  
  if (entity.type === 'Point') {
    geometry = {
      type: 'Point',
      coordinates: entity.coordinates
    };
  } else if (entity.type === 'LineString') {
    geometry = {
      type: 'LineString',
      coordinates: entity.coordinates
    };
  } else {
    return null;
  }
  
  return {
    type: 'Feature',
    geometry: geometry,
    properties: {
      layer: entity.layer || '0'
    }
  };
}
